import { deepEquals, isPureObject } from '../object';
import { flatten, clone, dissocPath } from 'ramda';

/**
 *
 * A utility function for deep comparison between two objects to identify differences.
 * It recursively navigates through all levels of the objects to find differences in values,
 * including those within nested objects. The comparison does not consider the order of keys
 * and only compares objects and null values (arrays are not compared if their values have changed).
 *
 * Note: This function performs a deep comparison on objects and a strict comparison on non-object values.
 *
 * @param obj1 The first object to compare.
 * @param obj2 The second object to compare.
 * @param fieldsToOmit Optional array of field paths to omit from comparison (e.g., [['field1'], ['nested', 'field2']])
 * @returns An object with three properties:
 *  - differenceCount: a number representing the total count of differences found.
 *  - dataIsNotEqual: a boolean flag indicating if any difference has been detected.
 *  - changedPaths: an array of paths representing the paths to keys that have been changed
 *
 */
const findDifference = ({
  obj1: _obj1 = {},
  obj2: _obj2 = {},
  fieldsToOmit = [],
}: {
  obj1: $Object;
  obj2: $Object;
  fieldsToOmit?: Array<Array<string>>;
}): {
  /** Total count of differences found */
  differenceCount: number;

  /** True if any difference has been detected */
  dataIsNotEqual: boolean;

  /** Paths for changed keys. In { x:1, y: { z: 1 } } the path returned for `z` would be ['y', 'z'] */
  changedPaths: Array<Array<string>>;
} => {
  // Apply field omission logic before comparison
  const getOmitted = (obj: $Object | null = {}) =>
    omitKeys(obj || {}, fieldsToOmit);

  const obj1 = getOmitted(_obj1);
  const obj2 = getOmitted(_obj2);

  let differenceCount = 0;
  let dataIsNotEqual = false;
  const changedPaths: Array<Array<string>> = [];

  for (const key in obj1) {
    if (
      !isPureObject(obj1[key]) ||
      !isPureObject(obj2[key]) ||
      obj1[key] === null
    ) {
      const notEqual = obj1[key] !== obj2[key];
      differenceCount = notEqual ? differenceCount + 1 : differenceCount;

      if (notEqual) {
        dataIsNotEqual = notEqual;
        changedPaths.push([key]);
      }
    } else {
      const equal = deepEquals(obj1[key], obj2[key]);

      if (!equal) {
        for (const deepKey in obj1[key]) {
          if (
            isPureObject(obj1[key][deepKey]) &&
            isPureObject(obj2[key][deepKey]) &&
            obj1[key][deepKey] !== null
          ) {
            const deepDiff = findDifference({
              obj1: obj1[key][deepKey],
              obj2: obj2[key][deepKey],
              // Don't pass fieldsToOmit to nested calls as they're already omitted
            });

            differenceCount += deepDiff.differenceCount;

            if (deepDiff.changedPaths)
              deepDiff.changedPaths.forEach(path => {
                changedPaths.push([key, deepKey, ...flatten(path)]);
              });
          } else {
            if (obj1[key][deepKey] !== obj2[key][deepKey]) {
              differenceCount += 1;
              changedPaths.push([key, deepKey]);
            }
          }
        }

        dataIsNotEqual = true;
      }
    }
  }

  return {
    differenceCount,
    dataIsNotEqual,
    changedPaths,
  };
};

/**
 * Helper function to omit specified keys from an object
 * @param obj The object to omit keys from
 * @param paths Array of paths to omit (e.g., [['field1'], ['nested', 'field2']])
 * @returns Object with specified keys omitted
 */
const omitKeys = (obj: $Object, paths: Array<Array<string>>) => {
  let newObj = clone(obj);

  paths.forEach(path => {
    const dissocPathResult = dissocPath(path, newObj);
    newObj = dissocPathResult as $Object;
  });

  return newObj;
};

export default findDifference;
